import type { CapabilityReason, CapabilityStatus } from '../api/client';
/** Human copy for each verdict reason code (never the raw code). */
export const REASON_COPY: Record<CapabilityReason, string> = {
  meets_floor: 'Your model met the capability floor for AI journey modeling.',
  below_floor:
    'Your model scored below the capability floor, so AI journey modeling may be lower quality.',
  context_below_minimum:
    'This model’s context window is below the 8,192-token minimum for reliable journey modeling.',
  context_unknown:
    'We couldn’t confirm this model’s context window, so we can’t verify it.',
  insufficient_sample:
    'The evaluation set is still being finalized, so a floor result isn’t available yet.',
  scorer_error: 'The capability check couldn’t be scored reliably.',
  incomplete_run: 'The capability check didn’t complete every evaluation case.',
  inference_failed: 'A provider call failed during the capability check.',
};

/** One-line summary per verdict, used in the live-region announcement. */
export const VERDICT_SUMMARY: Record<CapabilityStatus, string> = {
  verified: 'Verified',
  limited: 'Limited',
  inconclusive: 'Inconclusive',
};

export function reasonCopy(reason: CapabilityReason | null): string {
  return reason ? REASON_COPY[reason] : '';
}

/** Human reason for a verdict, with a status fallback so copy is never empty (a11y). */
const VERDICT_FALLBACK: Record<CapabilityStatus, string> = {
  verified: 'Your model met the capability floor for AI journey modeling.',
  limited: 'Your model is usable but below the capability floor.',
  inconclusive: 'We couldn’t determine your model’s capability. You can try again.',
};

export function verdictReason(status: CapabilityStatus, reason: CapabilityReason | null): string {
  return reason ? REASON_COPY[reason] : VERDICT_FALLBACK[status];
}

/**
 * Exact micro-USD integer string → USD display (`~$X.XXXXXX`, trailing zeros
 * trimmed) with no precision loss. Rejects non-numeric or implausibly long input
 * (fail-closed) so a malformed API amount can't cause a bad render.
 */
export function formatMicroUsd(micro: string | null): string | null {
  if (micro === null || !/^\d{1,15}$/.test(micro)) return null;
  const n = BigInt(micro);
  const dollars = n / 1_000_000n;
  const frac = (n % 1_000_000n).toString().padStart(6, '0').replace(/0+$/, '');
  return frac.length > 0 ? `~$${dollars}.${frac}` : `~$${dollars}`;
}
